import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { login as loginApi, logout as logoutApi } from "@/api/auth";
import { TLoginRequest } from "@/api/auth/schema";
import { TLoginResponse } from "@/api/auth/type";
import { TUserData } from "@/common/types/user";
import { calculateMaxAge } from "@/common/utils/calculate-max-age";
import { decodeJwt } from "@/common/utils/jwt";
import { httpClient } from "@/libs/axios";
import { SessionAuthCookies } from "@/libs/cookies";

type SessionContextType = {
  isAuthenticated: boolean;
  user: TUserData | null;
  token: string | null;
  login: (credentials: TLoginRequest) => Promise<void>;
  setSessionFromResponse: (response: TLoginResponse) => void;
  logout: () => Promise<void>;
  isLoading: boolean;
};

type TokenRefreshedEvent = CustomEvent<{ accessToken: string }>;

const SessionContext = createContext<SessionContextType | null>(null);

function initializeSession() {
  const storedToken = SessionAuthCookies.get();
  if (!storedToken) {
    return { token: null, user: null, isAuthenticated: false };
  }

  try {
    const decodedUser = decodeJwt<TUserData>(storedToken);

    if (decodedUser.exp && decodedUser.exp * 1000 <= Date.now()) {
      clearSession();
      return { token: null, user: null, isAuthenticated: false };
    }

    httpClient.defaults.headers.common.Authorization = `Bearer ${storedToken}`;
    return {
      token: storedToken,
      // JWT uses "userId" claim; map it to the expected "id" field
      user: { ...decodedUser, id: (decodedUser as unknown as { userId: string }).userId },
      isAuthenticated: true,
    };
  } catch (error) {
    console.error("Failed to restore session:", error);
    clearSession();
    return { token: null, user: null, isAuthenticated: false };
  }
}

function clearSession() {
  SessionAuthCookies.remove();
  localStorage.removeItem("refresh_token");
  httpClient.defaults.headers.common.Authorization = undefined;
}

/**
 * SessionProvider manages authentication state for the application
 */
export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [sessionState, setSessionState] = useState(() => initializeSession());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    function handleUnauthorized() {
      setSessionState({ token: null, user: null, isAuthenticated: false });
    }

    function handleTokenRefreshed(event: Event) {
      const { accessToken } = (event as TokenRefreshedEvent).detail;
      const decodedUser = decodeJwt<TUserData>(accessToken);
      setSessionState((prev) => ({
        ...prev,
        token: accessToken,
        user: decodedUser,
      }));
    }

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    window.addEventListener("auth:token-refreshed", handleTokenRefreshed);
    return () => {
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
      window.removeEventListener("auth:token-refreshed", handleTokenRefreshed);
    };
  }, []);

  const setSessionFromResponse = useCallback((response: TLoginResponse) => {
    const { accessToken, refreshToken, user: apiUser } = response.data;

    const decodedUser = decodeJwt<TUserData>(accessToken);
    const maxAge = calculateMaxAge(decodedUser.exp);

    SessionAuthCookies.set(accessToken, { maxAge });
    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken);
    }
    httpClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    setSessionState({
      token: accessToken,
      user: {
        ...decodedUser,
        id: apiUser.id,
        email: apiUser.email,
      },
      isAuthenticated: true,
    });
  }, []);

  const login = useCallback(
    async (credentials: TLoginRequest) => {
      try {
        setIsLoading(true);
        const response = await loginApi(credentials);
        setSessionFromResponse(response);
      } catch (error) {
        console.error("Login failed:", error);
        clearSession();
        setSessionState({ token: null, user: null, isAuthenticated: false });
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [setSessionFromResponse]
  );

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        await logoutApi({ refreshToken });
      }
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      clearSession();
      setSessionState({ token: null, user: null, isAuthenticated: false });
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      setIsLoading(false);
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo(
    () => ({
      isAuthenticated: sessionState.isAuthenticated,
      user: sessionState.user,
      token: sessionState.token,
      login,
      setSessionFromResponse,
      logout,
      isLoading,
    }),
    [
      sessionState.isAuthenticated,
      sessionState.user,
      sessionState.token,
      login,
      setSessionFromResponse,
      logout,
      isLoading,
    ]
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}

/**
 * Hook to access session context
 * @throws {Error} If used outside SessionProvider
 */
export function useSession(): SessionContextType {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}
